import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    EmptyState,
    FilterBar,
    FlussPageHeader,
    FlussStatusChip,
} from "modules/fluss/components/design-kit/FlussDesignKit";
import { flussApi, flussApiError } from "modules/fluss/services/fluss-api";
import { flussDefinitionLabel } from "modules/fluss/utils/flussDefinitionLabel";
import { currentNodeLabel } from "modules/fluss/utils/currentNodeLabel";
import { StartInput } from "modules/fluss/utils/startInputs";

const FlussRequestsPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [startOpen, setStartOpen] = useState(false);
    const [definitionKey, setDefinitionKey] = useState("");
    const [inputValues, setInputValues] = useState<Record<string, string | boolean>>({});
    const [inputErrors, setInputErrors] = useState<Record<string, string>>({});
    const definitions = useQuery({
        queryKey: ["fluss", "definitions", "startable"],
        queryFn: flussApi.listStartableDefinitions,
        enabled: startOpen,
    });
    const startable = (definitions.data || []).filter((definition) => definition.startable_manually);
    const selectedDefinition = startable.find((definition) => definition.definition_key === definitionKey);
    const startInputs = selectedDefinition?.start_inputs || [];
    const initialValues = (inputs: StartInput[]) => Object.fromEntries(inputs
        .filter((input) => input.default !== undefined && input.default !== null && input.default !== "")
        .map((input) => [input.name, input.type === "boolean" ? input.default === true || input.default === "true" : String(input.default)]));
    const start = useMutation({
        mutationFn: (variables?: Record<string, string | number | boolean>) => variables
            ? flussApi.startInstance(definitionKey, variables) : flussApi.startInstance(definitionKey),
        onSuccess: (instance) => {
            setStartOpen(false);
            setDefinitionKey("");
            setInputValues({});
            setInputErrors({});
            queryClient.invalidateQueries({ queryKey: ["fluss", "requests"] });
            navigate(`/home/fluss/requests/${instance.id}`);
        },
        onError: (error) => {
            const apiError = flussApiError(error);
            if (apiError.code === "START_INPUT_INVALID") setInputErrors(apiError.details || {});
        },
    });
    const closeStart = () => {
        setStartOpen(false);
        setDefinitionKey("");
        setInputValues({});
        setInputErrors({});
        start.reset();
    };
    const submitStart = () => {
        const errors: Record<string, string> = {};
        const variables: Record<string, string | number | boolean> = {};
        for (const input of startInputs) {
            const raw = inputValues[input.name];
            if (raw === undefined || raw === "") {
                if (input.required) errors[input.name] = String(t("fluss.requests.input_required", { defaultValue: "Informe este campo." }));
                continue;
            }
            if (input.type === "number") {
                const number = Number(raw);
                if (!Number.isFinite(number)) errors[input.name] = String(t("fluss.requests.input_number_invalid", { defaultValue: "Informe um número válido." }));
                else variables[input.name] = number;
            } else variables[input.name] = raw;
        }
        setInputErrors(errors);
        if (!Object.keys(errors).length) start.mutate(startInputs.length ? variables : undefined);
    };
    const requests = useQuery({
        queryKey: ["fluss", "requests", status],
        queryFn: () => flussApi.listRequests(status ? { status } : {}),
    });

    const filtered = useMemo(() => {
        const withLabel = (requests.data || []).map((instance) => ({
            instance,
            definitionLabel: flussDefinitionLabel(t, instance.definition_name),
        }));
        const term = search.trim().toLocaleLowerCase();
        if (!term) return withLabel;
        return withLabel.filter(({ instance, definitionLabel }) =>
            [definitionLabel, currentNodeLabel(instance)]
                .filter(Boolean)
                .some((value) => String(value).toLocaleLowerCase().includes(term))
        );
    }, [requests.data, search, t]);

    const formatDate = (value?: string | null) =>
        value ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—";

    return (
        <Box className="fluss-page">
            <Stack gap={2}>
                <FlussPageHeader
                    title={t("fluss.requests.title", { defaultValue: "My requests" })}
                    subtitle={t("fluss.requests.subtitle", { defaultValue: "Follow the progress of processes you started." })}
                    actions={<Button variant="contained" onClick={() => setStartOpen(true)}>{t("fluss.requests.start", { defaultValue: "Start process" })}</Button>}
                />
                <FilterBar search={search} onSearch={setSearch}>
                    <Select
                        size="small"
                        value={status}
                        displayEmpty
                        onChange={(event) => setStatus(event.target.value)}
                        inputProps={{ "aria-label": String(t("fluss.monitor.status_filter", { defaultValue: "Status filter" })) }}
                        sx={{ minWidth: 180 }}
                    >
                        <MenuItem value="">{t("fluss.common.all_statuses", { defaultValue: "All statuses" })}</MenuItem>
                        {["running", "waiting", "completed", "cancelled", "incident", "suspended"].map((value) => (
                            <MenuItem key={value} value={value}>
                                {t(`fluss.status.${value}`, { defaultValue: value })}
                            </MenuItem>
                        ))}
                    </Select>
                </FilterBar>
                {!requests.isLoading && filtered.length === 0 ? (
                    <EmptyState
                        title={t("fluss.requests.empty_title_v2", { defaultValue: "You haven't started any processes yet." })}
                        description={t("fluss.requests.empty_description_v2", { defaultValue: "Start a process to follow its progress here." })}
                        actionLabel={t("fluss.requests.start", { defaultValue: "Start process" })}
                        onAction={() => setStartOpen(true)}
                    />
                ) : (
                    <Paper variant="outlined">
                        <List disablePadding>
                            {filtered.map(({ instance, definitionLabel }) => (
                                <ListItemButton
                                    key={instance.id}
                                    divider
                                    onClick={() => navigate(`/home/fluss/requests/${instance.id}`)}
                                >
                                    <ListItemText
                                        primary={definitionLabel}
                                        secondary={
                                            <Stack component="span" direction={{ xs: "column", sm: "row" }} gap={{ xs: 0, sm: 2 }}>
                                                <Typography component="span" variant="body2">
                                                    {t("fluss.requests.current_stage", { stage: currentNodeLabel(instance), defaultValue: "Current stage: {{stage}}" })}
                                                </Typography>
                                                <Typography component="span" variant="body2" color="text.secondary">
                                                    {t("fluss.requests.started_at", { date: formatDate(instance.started_at), defaultValue: "Started: {{date}}" })}
                                                </Typography>
                                            </Stack>
                                        }
                                    />
                                    <FlussStatusChip status={instance.end_reason === "terminated" ? "terminated" : instance.status} />
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>
                )}
            </Stack>
            <Dialog open={startOpen} onClose={closeStart} fullWidth maxWidth="sm">
                <DialogTitle>{t("fluss.requests.start", { defaultValue: "Start process" })}</DialogTitle>
                <DialogContent>
                    <Stack gap={2} sx={{ mt: 1 }}>
                        {definitions.isError && <Alert severity="error">{t("fluss.requests.definitions_error", { defaultValue: "Could not load processes. Close and try again." })}</Alert>}
                        {!definitions.isLoading && !definitions.isError && startable.length === 0 && (
                            <Alert severity="info">{t("fluss.requests.no_startable_for_you", { defaultValue: "No process is available for you to start." })}</Alert>
                        )}
                        {startable.length > 0 && (
                            <Select fullWidth displayEmpty value={definitionKey} onChange={(event) => {
                                const next = event.target.value;
                                setDefinitionKey(next);
                                setInputValues(initialValues(startable.find((definition) => definition.definition_key === next)?.start_inputs || []));
                                setInputErrors({});
                                start.reset();
                            }}
                                inputProps={{ "aria-label": String(t("fluss.requests.choose_process", { defaultValue: "Choose a process" })) }}>
                                <MenuItem value="" disabled>{t("fluss.requests.choose_process", { defaultValue: "Choose a process" })}</MenuItem>
                                {startable.map((definition) => (
                                    <MenuItem key={definition.id} value={definition.definition_key}>{flussDefinitionLabel(t, definition.name)}</MenuItem>
                                ))}
                            </Select>
                        )}
                        {startInputs.map((input) => input.type === "boolean" ? (
                            <Stack key={input.name}>
                                <FormControlLabel label={input.label} control={<Switch checked={inputValues[input.name] === true}
                                    onChange={(event) => {
                                        setInputValues((current) => ({ ...current, [input.name]: event.target.checked }));
                                        setInputErrors((current) => ({ ...current, [input.name]: "" }));
                                    }} />} />
                                {inputErrors[input.name] && <Typography variant="caption" color="error">{inputErrors[input.name]}</Typography>}
                            </Stack>
                        ) : (
                            <TextField key={input.name} fullWidth label={input.label} required={input.required}
                                type={input.type === "number" ? "number" : "text"} value={inputValues[input.name] ?? ""}
                                error={Boolean(inputErrors[input.name])} helperText={inputErrors[input.name]}
                                onChange={(event) => {
                                    setInputValues((current) => ({ ...current, [input.name]: event.target.value }));
                                    setInputErrors((current) => ({ ...current, [input.name]: "" }));
                                }} />
                        ))}
                        {start.isError && <Alert severity="error">{flussApiError(start.error).code === "NO_ACTIVE_DEFINITION"
                            ? t("fluss.requests.no_active_definition", { defaultValue: "This process is no longer available. Close and choose another process." })
                            : flussApiError(start.error).code === "START_NOT_ALLOWED"
                            ? t("fluss.requests.start_not_allowed", { defaultValue: "Você não tem permissão para iniciar este processo. Fale com o responsável pelo processo." })
                            : flussApiError(start.error).code === "PROCESS_START_FAILED"
                            ? flussApiError(start.error).message || t("fluss.requests.process_start_failed", { defaultValue: "Não foi possível iniciar o processo. Avise o analista responsável." })
                            : flussApiError(start.error).code === "START_INPUT_INVALID"
                            ? t("fluss.requests.input_invalid", { defaultValue: "Revise os dados informados para iniciar o processo." })
                            : t("fluss.requests.start_error", { defaultValue: "Could not start this process. Try again." })}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeStart}>{t("fluss.common.cancel", { defaultValue: "Cancel" })}</Button>
                    <Button variant="contained" disabled={!definitionKey || start.isPending} onClick={submitStart}>
                        {t("fluss.requests.start", { defaultValue: "Start process" })}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FlussRequestsPage;
